import React, { useEffect, useState, useCallback, useMemo } from "react"
import { Link } from "react-router-dom"
import classnames from "classnames"
import { Card, CardBody, Col, Row, Spinner } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import DatePicker from "react-datepicker"
import Breadcrumbs from "components/Common/Breadcrumb"
import { post } from "helpers/api_helper"
import { GET_AI_USAGE_LOG } from "helpers/url_helper"
import moment from "moment"
import "react-datepicker/dist/react-datepicker.css"

const DASHBOARD_PAGE_SIZE = 5000

const RANGE_OPTIONS = [
  { value: "7d", label: "7D", days: 7 },
  { value: "30d", label: "30D", days: 30 },
  { value: "90d", label: "90D", days: 90 },
]

const CHART_COLORS = [
  "#556ee6",
  "#34c38f",
  "#f46a6a",
  "#f1b44c",
  "#50a5f1",
  "#a3a3a3",
]

// Blended $/1K-token estimate used only when the API row has no explicit cost field.
const MODEL_RATE_PER_1K_TOKENS = {
  "gpt-4o": 0.01,
  "gpt-4": 0.03,
  "gpt-3.5": 0.002,
  "claude-3-opus": 0.03,
  "claude-3-sonnet": 0.006,
  "claude-3-haiku": 0.0015,
  claude: 0.006,
  gemini: 0.005,
}
const DEFAULT_RATE_PER_1K_TOKENS = 0.005

// 100% mark for the "Today's Usage by User" meter below.
const DAILY_TOKEN_LIMIT_PER_USER = 200000000 

const usagePctColor = pct => {
  if (pct >= 90) return "#f46a6a"
  if (pct >= 70) return "#f1b44c"
  return "#34c38f"
}

const extractRows = response => {
  const rows =
    response?.data ||
    response?.datas ||
    response?.items ||
    response?.result ||
    (Array.isArray(response) ? response : [])
  return Array.isArray(rows) ? rows : []
}

const getRowDate = row =>
  row.createdAt ??
  row.createdat ??
  row.logDate ??
  row.logdate ??
  row.date ??
  row.timestamp

const getRowModel = row =>
  row.modelsused || row.model || row.modelName || row.modelname || row.aiModel || "Unknown"

const getRowUser = row =>
  row.userName ||
  row.username ||
  row.userEmail ||
  row.useremail ||
  (row.userid || row.userId ? `User ${row.userid ?? row.userId}` : "Unknown")

const getRowLogType = row => row.logType || row.logtype || "Unknown"

const getRowLogName = row => row.logName || row.logname || "Unknown"

// Rows come pre-aggregated per logType (date / project / model / session / blocks).
// Feeding a chart the wrong segment would double-count, so each chart pulls only
// the rows matching its logType. If the dataset has no logType at all (older data),
// filtering is skipped so the dashboard still works.
const hasLogTypes = rows => rows.some(r => r.logType || r.logtype)

const filterByLogType = (rows, type) =>
  hasLogTypes(rows)
    ? rows.filter(r => getRowLogType(r).toLowerCase() === type)
    : rows

const getRowTokens = row => {
  const input = Number(row.inputTokens ?? row.inputtokens ?? 0)
  const output = Number(row.outputTokens ?? row.outputtokens ?? 0)
  const total = Number(row.totalTokens ?? row.totaltokens ?? input + output)
  return { input, output, total }
}

const getRowCost = row => {
  const explicit = row.cost ?? row.price ?? row.amount ?? row.totalCost ?? row.totalcost
  if (explicit != null && !isNaN(explicit)) return Number(explicit)

  const { total } = getRowTokens(row)
  const model = getRowModel(row).toLowerCase()
  const rateKey = Object.keys(MODEL_RATE_PER_1K_TOKENS).find(k =>
    model.includes(k)
  )
  const rate = rateKey
    ? MODEL_RATE_PER_1K_TOKENS[rateKey]
    : DEFAULT_RATE_PER_1K_TOKENS
  return (total / 1000) * rate
}

const buildDashboardMetrics = rows => {
  const byDay = {}
  const byModel = {}
  const byUser = {}
  const byUserDay = {}
  const byLogName = {}
  let totalRequests = 0
  let totalTokens = 0
  let totalCost = 0

  rows.forEach(row => {
    const dateVal = getRowDate(row)
    const day = moment(dateVal).isValid()
      ? moment(dateVal).format("YYYY-MM-DD")
      : "Unknown"
    const { total } = getRowTokens(row)
    const cost = getRowCost(row)
    const model = getRowModel(row)
    const user = getRowUser(row)
    const logName = getRowLogName(row)

    totalRequests += 1
    totalTokens += total
    totalCost += cost

    if (!byDay[day]) byDay[day] = { requests: 0, tokens: 0, cost: 0 }
    byDay[day].requests += 1
    byDay[day].tokens += total
    byDay[day].cost += cost

    if (!byModel[model]) byModel[model] = { tokens: 0, cost: 0 }
    byModel[model].tokens += total
    byModel[model].cost += cost

    if (!byUser[user]) byUser[user] = { tokens: 0, requests: 0 }
    byUser[user].tokens += total
    byUser[user].requests += 1

    if (!byUserDay[user]) byUserDay[user] = {}
    byUserDay[user][day] = (byUserDay[user][day] || 0) + total

    if (!byLogName[logName]) byLogName[logName] = { tokens: 0, requests: 0, cost: 0 }
    byLogName[logName].tokens += total
    byLogName[logName].requests += 1
    byLogName[logName].cost += cost
  })

  return {
    totalRequests,
    totalTokens,
    totalCost,
    byDay,
    byModel,
    byUser,
    byUserDay,
    byLogName,
  }
}

const pctChange = (curr, prev) => {
  if (!prev) return curr > 0 ? 100 : 0
  return ((curr - prev) / prev) * 100
}

const formatNumber = n =>
  (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })

const formatCurrency = n =>
  `$${(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const StatCard = ({ title, icon, value, change, loading }) => {
  const isUp = (change ?? 0) >= 0
  return (
    <Col xl={3} md={6}>
      <Card className="mini-stats-wid">
        <CardBody>
          <div className="d-flex">
            <div className="flex-grow-1">
              <p className="text-muted fw-medium">{title}</p>
              {loading ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <h4 className="mb-2">{value}</h4>
              )}
              {!loading && change != null && (
                <>
                  <span
                    className={`badge badge-soft-${
                      isUp ? "success" : "danger"
                    } font-size-12`}
                  >
                    <i
                      className={`mdi mdi-arrow-${isUp ? "up" : "down"} me-1`}
                    />
                    {Math.abs(change).toFixed(1)}%
                  </span>{" "}
                  <span className="text-muted font-size-11">
                    vs previous period
                  </span>
                </>
              )}
            </div>
            <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
              <span className="avatar-title rounded-circle bg-primary">
                <i className={`bx ${icon} font-size-24`} />
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  )
}

const ChartPanel = ({ title, headerRight, loading, isEmpty, height = 300, children }) => (
  <Card>
    <CardBody>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <h5 className="card-title mb-0">{title}</h5>
        {headerRight}
      </div>
      <div style={{ minHeight: height }}>
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height }}
          >
            <Spinner color="primary" />
          </div>
        ) : isEmpty ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted"
            style={{ height }}
          >
            <i className="bx bx-bar-chart-alt-2 font-size-24 mb-2" />
            <span>No data available for this period.</span>
          </div>
        ) : (
          children
        )}
      </div>
    </CardBody>
  </Card>
)

const AIUsageDashboard = () => {
  document.title = "AI Usage Dashboard | FlexiTracBoard"

  const [dashboardRange, setDashboardRange] = useState("30d")
  const [trendMetric, setTrendMetric] = useState("tokens")
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardRows, setDashboardRows] = useState([])
  const [dashboardPrevRows, setDashboardPrevRows] = useState([])
  const [dashboardRangeDates, setDashboardRangeDates] = useState(null)
  const [usageByDateSelected, setUsageByDateSelected] = useState(new Date())

  // Reuses the same GET_AI_USAGE_LOG endpoint as the detailed report page,
  // just with a wider page size and its own date window, plus a matching
  // prior window fetch to compute the % change stat cards.
  const fetchDashboardData = useCallback(async range => {
    setDashboardLoading(true)
    const rangeOpt = RANGE_OPTIONS.find(r => r.value === range) || RANGE_OPTIONS[1]
    const days = rangeOpt.days

    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - (days - 1))
    const prevEnd = new Date(start)
    prevEnd.setDate(start.getDate() - 1)
    const prevStart = new Date(prevEnd)
    prevStart.setDate(prevEnd.getDate() - (days - 1))

    const buildPayload = (from, to) => ({
      userid: 0,
      fromdate: moment(from).format("YYYY-MM-DD"),
      todate: moment(to).add(1, "days").format("YYYY-MM-DD"),
      logtype: "",
      projectfolderpath: "",
      page: 1,
      pagesize: DASHBOARD_PAGE_SIZE,
    })

    try {
      const [currRes, prevRes] = await Promise.all([
        post(GET_AI_USAGE_LOG, buildPayload(start, end)),
        post(GET_AI_USAGE_LOG, buildPayload(prevStart, prevEnd)),
      ])
      setDashboardRows(extractRows(currRes))
      setDashboardPrevRows(extractRows(prevRes))
      setDashboardRangeDates({ start, end })
    } catch {
      setDashboardRows([])
      setDashboardPrevRows([])
      setDashboardRangeDates({ start, end })
    } finally {
      setDashboardLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboardData(dashboardRange)
  }, [dashboardRange, fetchDashboardData])

  // Keep the "Usage by Date" selection inside the currently loaded range
  // whenever the range changes (e.g. switching 7D -> 30D).
  useEffect(() => {
    if (!dashboardRangeDates) return
    const { start, end } = dashboardRangeDates
    setUsageByDateSelected(prev => {
      if (prev && moment(prev).isBetween(start, end, "day", "[]")) return prev
      return end
    })
  }, [dashboardRangeDates])

  // Token-usage analytics run off the "date" logType segment so daily/user/date
  // aggregates are not inflated by the project/model/session rows in the same feed.
  const dateRows = useMemo(
    () => filterByLogType(dashboardRows, "date"),
    [dashboardRows]
  )
  const prevDateRows = useMemo(
    () => filterByLogType(dashboardPrevRows, "date"),
    [dashboardPrevRows]
  )
  // Model charts use the "model" segment, project chart uses the "project" segment.
  const modelRows = useMemo(
    () => filterByLogType(dashboardRows, "model"),
    [dashboardRows]
  )
  const projectRows = useMemo(
    () => filterByLogType(dashboardRows, "project"),
    [dashboardRows]
  )

  const dashboardMetrics = useMemo(
    () => buildDashboardMetrics(dateRows),
    [dateRows]
  )
  const prevMetrics = useMemo(
    () => buildDashboardMetrics(prevDateRows),
    [prevDateRows]
  )
  const modelMetrics = useMemo(
    () => buildDashboardMetrics(modelRows),
    [modelRows]
  )
  const projectMetrics = useMemo(
    () => buildDashboardMetrics(projectRows),
    [projectRows]
  )

  const dailySeries = useMemo(() => {
    if (!dashboardRangeDates) return { categories: [], requests: [], tokens: [], cost: [] }
    const categories = []
    const requests = []
    const tokens = []
    const cost = []
    const cursor = moment(dashboardRangeDates.start)
    const end = moment(dashboardRangeDates.end)
    while (cursor.isSameOrBefore(end, "day")) {
      const key = cursor.format("YYYY-MM-DD")
      const day = dashboardMetrics.byDay[key]
      categories.push(cursor.format("DD MMM"))
      requests.push(day ? day.requests : 0)
      tokens.push(day ? day.tokens : 0)
      cost.push(day ? Number(day.cost.toFixed(2)) : 0)
      cursor.add(1, "day")
    }
    return { categories, requests, tokens, cost }
  }, [dashboardMetrics, dashboardRangeDates])

  const modelBreakdown = useMemo(
    () =>
      Object.entries(modelMetrics.byModel).sort(
        (a, b) => b[1].tokens - a[1].tokens
      ),
    [modelMetrics]
  )

  const projectBreakdown = useMemo(() => {
    const entries = Object.entries(projectMetrics.byLogName).sort(
      (a, b) => b[1].tokens - a[1].tokens
    )
    const top = entries.slice(0, 10)
    // Ascending so the biggest bar sits at the top of the horizontal chart.
    return top.reverse()
  }, [projectMetrics])

  // Full user list (no "Others" bucket) sorted by token usage, desc.
  const userBreakdown = useMemo(
    () =>
      Object.entries(dashboardMetrics.byUser).sort(
        (a, b) => b[1].tokens - a[1].tokens
      ),
    [dashboardMetrics]
  )

  const userBreakdownAsc = useMemo(
    () => [...userBreakdown].sort((a, b) => a[1].tokens - b[1].tokens),
    [userBreakdown]
  )

  const topUsersForTrend = useMemo(
    () =>
      userBreakdown
        .filter(([name]) => name !== "Others")
        .slice(0, 5)
        .map(([name]) => name),
    [userBreakdown]
  )

  const userDailySeries = useMemo(() => {
    if (!dashboardRangeDates) return []
    const days = []
    const cursor = moment(dashboardRangeDates.start)
    const end = moment(dashboardRangeDates.end)
    while (cursor.isSameOrBefore(end, "day")) {
      days.push(cursor.format("YYYY-MM-DD"))
      cursor.add(1, "day")
    }
    return topUsersForTrend.map(user => ({
      name: user,
      data: days.map(d => dashboardMetrics.byUserDay[user]?.[d] || 0),
    }))
  }, [topUsersForTrend, dashboardMetrics, dashboardRangeDates])

  const usageByDatePct = useMemo(() => {
    const dateKey = moment(usageByDateSelected).format("YYYY-MM-DD")
    return Object.entries(dashboardMetrics.byUserDay)
      .map(([user, days]) => {
        const tokens = days[dateKey] || 0
        return { user, tokens, pct: (tokens / DAILY_TOKEN_LIMIT_PER_USER) * 100 }
      })
      .filter(u => u.tokens > 0)
      .sort((a, b) => b.pct - a.pct)
  }, [dashboardMetrics, usageByDateSelected])

  const usageByDateChartOptions = useMemo(() => {
    const maxPct = usageByDatePct.reduce((m, u) => Math.max(m, u.pct), 0)
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 3, barHeight: "60%", distributed: true },
      },
      dataLabels: {
        enabled: true,
        formatter: v => `${v.toFixed(1)}%`,
      },
      colors: usageByDatePct.map(u => usagePctColor(u.pct)),
      xaxis: {
        categories: usageByDatePct.map(u => u.user),
        max: Math.max(100, Math.ceil(maxPct / 10) * 10),
        labels: { formatter: v => `${v}%` },
      },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val, opts) =>
            `${val.toFixed(1)}% (${formatNumber(
              usageByDatePct[opts.dataPointIndex]?.tokens
            )} of ${formatNumber(DAILY_TOKEN_LIMIT_PER_USER)} tokens)`,
        },
      },
    }
  }, [usageByDatePct])

  const isDashboardEmpty = !dashboardLoading && dateRows.length === 0

  const trendChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      colors: [CHART_COLORS[0]],
      xaxis: { categories: dailySeries.categories },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 },
      },
      tooltip: { x: { show: true } },
    }),
    [dailySeries.categories]
  )

  const modelChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
      dataLabels: { enabled: false },
      colors: [CHART_COLORS[1]],
      xaxis: { categories: modelBreakdown.map(([name]) => name) },
      legend: { show: false },
    }),
    [modelBreakdown]
  )

  const projectChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "55%" } },
      dataLabels: { enabled: false },
      colors: [CHART_COLORS[5]],
      xaxis: { categories: projectBreakdown.map(([name]) => name) },
      legend: { show: false },
      tooltip: { y: { formatter: v => `${formatNumber(v)} tokens` } },
    }),
    [projectBreakdown]
  )

  const userChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      labels: userBreakdown.map(([name]) => name),
      colors: CHART_COLORS,
      legend: { position: "bottom" },
      dataLabels: { enabled: true },
    }),
    [userBreakdown]
  )

  const userBarChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
      dataLabels: { enabled: false },
      colors: [CHART_COLORS[4]],
      xaxis: { categories: userBreakdownAsc.map(([name]) => name) },
      legend: { show: false },
    }),
    [userBreakdownAsc]
  )

  const userDailyChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      colors: CHART_COLORS,
      xaxis: { categories: dailySeries.categories },
      legend: { position: "bottom" },
      tooltip: { x: { show: true } },
    }),
    [dailySeries.categories]
  )

  const costTrendChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      colors: [CHART_COLORS[3]],
      xaxis: { categories: dailySeries.categories },
      yaxis: { labels: { formatter: v => `$${v.toFixed(0)}` } },
      tooltip: { y: { formatter: v => formatCurrency(v) } },
    }),
    [dailySeries.categories]
  )

  const modelCostChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "55%" } },
      dataLabels: { enabled: false },
      colors: [CHART_COLORS[2]],
      xaxis: {
        categories: modelBreakdown.map(([name]) => name),
        labels: { formatter: v => `$${Number(v).toFixed(0)}` },
      },
      tooltip: { x: { formatter: v => v } },
      legend: { show: false },
    }),
    [modelBreakdown]
  )

  const avgCostPerRequest =
    dashboardMetrics.totalRequests > 0
      ? dashboardMetrics.totalCost / dashboardMetrics.totalRequests
      : 0
  const prevAvgCostPerRequest =
    prevMetrics.totalRequests > 0
      ? prevMetrics.totalCost / prevMetrics.totalRequests
      : 0

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Admin" breadcrumbItem="AI Usage Dashboard" />

          <div className="d-flex justify-content-end mb-3">
            <Link to="/aiusagereport" className="btn btn-outline-primary btn-sm">
              <i className="bx bx-table me-1" /> View Detailed Report
            </Link>
          </div>

          <Row>
            <StatCard
              title="Total Requests"
              icon="bx-data"
              loading={dashboardLoading}
              value={formatNumber(dashboardMetrics.totalRequests)}
              change={pctChange(
                dashboardMetrics.totalRequests,
                prevMetrics.totalRequests
              )}
            />
            <StatCard
              title="Total Tokens"
              icon="bx-microchip"
              loading={dashboardLoading}
              value={formatNumber(dashboardMetrics.totalTokens)}
              change={pctChange(
                dashboardMetrics.totalTokens,
                prevMetrics.totalTokens
              )}
            />
            <StatCard
              title="Total Cost"
              icon="bx-dollar-circle"
              loading={dashboardLoading}
              value={formatCurrency(dashboardMetrics.totalCost)}
              change={pctChange(
                dashboardMetrics.totalCost,
                prevMetrics.totalCost
              )}
            />
            <StatCard
              title="Avg Cost / Request"
              icon="bx-wallet"
              loading={dashboardLoading}
              value={formatCurrency(avgCostPerRequest)}
              change={pctChange(avgCostPerRequest, prevAvgCostPerRequest)}
            />
          </Row>

          <Row>
            <Col xl={6}>
              <ChartPanel
                title="Usage Over Time"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
                headerRight={
                  <div className="d-flex flex-wrap gap-2">
                    <ul className="nav nav-pills mb-0">
                      {["tokens", "requests"].map(m => (
                        <li className="nav-item" key={m}>
                          <Link
                            to="#"
                            className={classnames("nav-link py-1 px-2 font-size-12", {
                              active: trendMetric === m,
                            })}
                            onClick={() => setTrendMetric(m)}
                          >
                            {m === "tokens" ? "Tokens" : "Requests"}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <ul className="nav nav-pills mb-0">
                      {RANGE_OPTIONS.map(r => (
                        <li className="nav-item" key={r.value}>
                          <Link
                            to="#"
                            className={classnames("nav-link py-1 px-2 font-size-12", {
                              active: dashboardRange === r.value,
                            })}
                            onClick={() => setDashboardRange(r.value)}
                          >
                            {r.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <ReactApexChart
                  options={trendChartOptions}
                  series={[
                    {
                      name: trendMetric === "tokens" ? "Tokens" : "Requests",
                      data:
                        trendMetric === "tokens"
                          ? dailySeries.tokens
                          : dailySeries.requests,
                    },
                  ]}
                  type="area"
                  height={300}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
            <Col xl={6}>
              <ChartPanel
                title="Usage by Model"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={modelChartOptions}
                  series={[
                    { name: "Tokens", data: modelBreakdown.map(([, v]) => v.tokens) },
                  ]}
                  type="bar"
                  height={300}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <ChartPanel
                title="Top Projects"
                loading={dashboardLoading}
                isEmpty={!dashboardLoading && projectBreakdown.length === 0}
                height={Math.max(300, projectBreakdown.length * 36)}
              >
                <ReactApexChart
                  options={projectChartOptions}
                  series={[
                    { name: "Tokens", data: projectBreakdown.map(([, v]) => v.tokens) },
                  ]}
                  type="bar"
                  height={Math.max(300, projectBreakdown.length * 36)}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <ChartPanel
                title="Usage by Date"
                loading={dashboardLoading}
                isEmpty={!dashboardLoading && usageByDatePct.length === 0}
                headerRight={
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <DatePicker
                      dateFormat="yyyy-MM-dd"
                      selected={usageByDateSelected}
                      onChange={setUsageByDateSelected}
                      className="form-control date-icon-back"
                      minDate={dashboardRangeDates?.start}
                      maxDate={dashboardRangeDates?.end}
                      disabled={!dashboardRangeDates}
                    />
                    <span className="text-muted font-size-12">
                      100% = {formatNumber(DAILY_TOKEN_LIMIT_PER_USER)} tokens/user/day
                    </span>
                  </div>
                }
                height={Math.max(220, usageByDatePct.length * 40)}
              >
                <ReactApexChart
                  options={usageByDateChartOptions}
                  series={[{ name: "Usage", data: usageByDatePct.map(u => Number(u.pct.toFixed(1))) }]}
                  type="bar"
                  height={Math.max(220, usageByDatePct.length * 40)}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>

          <Row>
            <Col xl={6}>
              <ChartPanel
                title="Usage by User"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={userChartOptions}
                  series={userBreakdown.map(([, v]) => v.tokens)}
                  type="donut"
                  height={300}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
            <Col xl={6}>
              <ChartPanel
                title="Usage by User (Bar)"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={userBarChartOptions}
                  series={[
                    { name: "Tokens", data: userBreakdownAsc.map(([, v]) => v.tokens) },
                  ]}
                  type="bar"
                  height={300}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <ChartPanel
                title="Daily Usage by User"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={userDailyChartOptions}
                  series={userDailySeries}
                  type="line"
                  height={320}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>

          <Row>
            <Col xl={6}>
              <ChartPanel
                title="Cost Trend"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={costTrendChartOptions}
                  series={[{ name: "Cost ($)", data: dailySeries.cost }]}
                  type="line"
                  height={300}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
            <Col xl={6}>
              <ChartPanel
                title="Cost Breakdown by Model"
                loading={dashboardLoading}
                isEmpty={isDashboardEmpty}
              >
                <ReactApexChart
                  options={modelCostChartOptions}
                  series={[
                    {
                      name: "Cost ($)",
                      data: modelBreakdown.map(([, v]) => Number(v.cost.toFixed(2))),
                    },
                  ]}
                  type="bar"
                  height={280}
                  className="apex-charts"
                />
              </ChartPanel>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  )
}

export default AIUsageDashboard
